import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CampaignAccessReward,
  ChannelInfo,
  PromocodeAttributes,
  RewardAttributes,
  RewardPeriod,
} from '@loyalty/shared-common/rewards-api-client';
import {
  ChannelResultData,
  SearchElementType,
  SearchLanguage,
} from '@qualifio/data-search-api-client';
import { Controller, useFormContext } from 'react-hook-form';
import { DataSearch } from '@qualifio/data-search-ui';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Module } from '@loyalty/shared-common/organisation-api-client';
import { Page } from '@qualifio/api-client';
import { QlfFormRow, QlfNumberField, colorPalette, useQlfTranslation } from '@qualifio/ui-commons';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { AdjustParticipationDialog } from './AdjustParticipationDialog';
import { CampaignRedeemerListItem } from './CampaignRedeemerListItem';
import { RewardListItemInfo } from '../sidebar/RewardListItemInfo';
import { SearchPromocodes } from './SearchPromocodes';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';
import { useFileDownload } from '../../../shared/use-file-download';
import { usePeriodValue } from '../../reward-constants';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useRewards } from '../../RewardsContext';
import { useSelectiveEffect } from '../../../shared/use-selective-effect';

const useStyles = makeStyles<Theme>(({ spacing, palette, shape }) => ({
  title: {
    padding: spacing(4, 8, 1),
    textAlign: 'center',
    '&:after': {
      backgroundColor: palette.primary.main,
      content: "''",
      display: 'block',
      height: 3,
      margin: spacing(1, 'auto', 0),
      width: 40,
    },
  },
  content: {
    padding: spacing(1, 8, 0.5),
    '&:first-of-type': {
      paddingTop: spacing(3),
    },
  },
  actions: {
    padding: spacing(0.5, 8, 4),
    justifyContent: 'flex-end',
  },
  closeButton: {
    fontSize: 16,
    padding: spacing(1.5),
    position: 'absolute',
    right: 0,
    top: 0,
  },
  field: {
    marginBottom: spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  languageInput: {
    minHeight: `calc(40px - ${spacing(0.4)}px)`,
    padding: spacing(0.2),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    borderRadius: shape.borderRadius,
    margin: 2,
  },
  chipAvatar: {
    height: 10,
    width: 10,
  },
  participationNumberField: {
    display: 'flex',
    alignItems: 'center',
  },
  link: {
    '& a': {
      textDecoration: 'none !important',
      color: colorPalette.blue.main,
    },
  },
}));

export function CampaignAccessDetails({
  reward,
}: {
  reward: CampaignAccessReward;
}): React.ReactElement {
  const classes = useStyles();
  const { features } = useFeatures();
  const { program } = useProgram();
  const { write: rewardWriteEnabled } = features[Feature.REWARD];
  const { enabled: rewardExportEnabled } = features[Feature.EXPORT_REWARDS];
  const [adjustParticipationDialogOpen, setAdjustParticipationDialogOpen] = useState<boolean>();
  const hasParticipationValueBeenEdited = !!reward?.data?.accessData.nbParticipations;
  const [participationValidityEditing, setParticipationValidityEditing] = useState<boolean>(false);
  const { currentAccount } = useAccounts();
  const { t } = useTranslation();
  const { t: htmlT } = useQlfTranslation('translation');
  const { selectedReward } = useRewards();
  const { control, watch } = useFormContext<RewardAttributes>();
  const periodValue = usePeriodValue();
  const { dataSearchApi, profileApi, rewardsApi } = useApi();
  const enableWonExport = rewardExportEnabled && !!selectedReward?.promocodeStats?.wonCodes;
  const { download, downloading: exporting, anchorRef: exportAnchorRef } = useFileDownload();

  if (!selectedReward) {
    throw new Error('No reward selected');
  }

  const rewardId = selectedReward.id;

  const downloadExport = useCallback(
    () => download(() => profileApi.exportWonRewards(currentAccount.id, rewardId)),
    [download, profileApi, currentAccount.id, rewardId],
  );

  useSelectiveEffect(
    () => {
      const validityPeriod = reward?.data?.accessData.validityPeriod;
      setParticipationValidityEditing(!validityPeriod?.unit || validityPeriod?.value === undefined);
    },
    [reward.id, reward?.data?.accessData.validityPeriod, reward.updatedAt],
    [reward.id, reward.updatedAt],
  );

  const rewarAttribute = watch();

  const permissionTooltip = !rewardWriteEnabled
    ? t('shared.action.permission.required')
    : selectedReward?.deletedAt
    ? t('rewards.archived.edit.forbidden')
    : '';

  async function doSearch(
    pageNumber: number,
    search?: string,
  ): Promise<Page<PromocodeAttributes> | undefined> {
    const profilesPage = search
      ? await profileApi.searchProfiles({
          params: {
            search,
            accountId: currentAccount.id,
            programId: program.id,
          },
        })
      : undefined;

    if (!!search && !profilesPage?.data?.length) {
      return undefined;
    } else {
      return rewardsApi.getPromocodes(reward.id, {
        params: {
          profileIds: profilesPage?.data?.map(p => p.id),
          pageNumber,
        },
      });
    }
  }

  return (
    <div>
      <Box>
        <Typography variant="h3">{t('rewards.campaign.title')}</Typography>
        <div>
          <Typography variant="body1">{t('rewards.campaign.message.intro1')}</Typography>
          <Typography variant="body1" className={classes.link}>
            {htmlT('rewards.campaign.message.intro2', {
              url: 'https://support.qualifio.com/hc/en-us/sections/203320057-Publish-campaigns',
            })}
          </Typography>
        </div>
      </Box>
      <Box>
        <DialogContent
          className={classes.content}
          sx={{
            borderBottom: `1px solid ${colorPalette.grey.main}`,
          }}
        >
          {program.languages.map(language => (
            <div key={language}>
              <Controller
                control={control}
                name={`data.channelInfoByLanguage.${language}`}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <QlfFormRow
                    label={
                      <img
                        src={`${BASE_PATH}/assets/template/assets/flags/${language}.svg`}
                        alt={language}
                        style={{ width: 21 }}
                      />
                    }
                    labelWidth={50}
                    mandatory
                  >
                    <DataSearch
                      {...field}
                      value={
                        field.value
                          ? {
                              type: SearchElementType.channel,
                              uniqueId: field.value.uniqueId,
                            }
                          : null
                      }
                      onChange={value =>
                        field.onChange(
                          value
                            ? ({
                                type: SearchElementType.channel,
                                uniqueId: value.identifier.uniqueId,
                                campaignId: (value.data as ChannelResultData).campaign.id, // needed later for generating secure token from rewards-api when consuming the reward
                              } as ChannelInfo)
                            : null,
                        )
                      }
                      dataSearchApiClient={dataSearchApi}
                      module={Module.LOYALTY}
                      externalTenantId={currentAccount.id}
                      entityId={program.id}
                      searchCriteria={{ channel: true }}
                      languages={[language.toUpperCase() as SearchLanguage]}
                      error={error}
                    />
                  </QlfFormRow>
                )}
              />
            </div>
          ))}
        </DialogContent>
        <Box display="flex" alignItems="center" mt={1}>
          <Box flexGrow={1}>
            <Typography variant="h3">{t('rewards.campaign.access.title')}</Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Box mr={1}>{t('rewards.campaign.access.redeemed')}</Box>
            <RewardListItemInfo
              won={rewarAttribute.promocodeStats?.wonCodes}
              total={rewarAttribute.promocodeStats?.totalCodes}
              sx={{ fontSize: '1.5em', fontWeight: 'bold' }}
            />
          </Box>
        </Box>
        <Typography variant="body1" style={{ marginTop: '10px' }}>
          {t('rewards.campaign.access.message.intro')}
        </Typography>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <DialogContent className={classes.content}>
            {!hasParticipationValueBeenEdited ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <QlfFormRow
                  label={t('reward.campaign.dialog.participation.number')}
                  classNames={{ field: classes.participationNumberField }}
                  mandatory
                >
                  <Controller
                    control={control}
                    name="data.accessData.nbParticipations"
                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                      <Tooltip title={permissionTooltip}>
                        <div>
                          <QlfNumberField
                            {...field}
                            inputRef={ref}
                            placeholder={t('reward.campaign.dialog.participation.number.value')}
                            error={!!error}
                            helperText={error?.message}
                            sx={{ width: 70 }}
                            disabled={!!selectedReward?.deletedAt}
                          />
                        </div>
                      </Tooltip>
                    )}
                  />
                  <Typography variant="body1" sx={{ marginLeft: 1 }}>
                    {t('reward.campaign.access.limitation.participation')}
                  </Typography>
                </QlfFormRow>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ width: 180 }}>
                    {t('reward.campaign.dialog.participation.number')}
                  </Typography>
                  <Typography variant="h3">{reward?.data?.accessData?.nbParticipations}</Typography>
                  <Typography sx={{ marginLeft: 1 }}>
                    {t('reward.campaign.access.limitation.participation')}
                  </Typography>
                  <Controller
                    control={control}
                    name="data.accessData.nbParticipations"
                    render={({ field: { ref, onChange, value } }) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={permissionTooltip}>
                          <div>
                            <IconButton
                              disabled={!rewardWriteEnabled || !!selectedReward?.deletedAt}
                              className={classes.iconButton}
                              onClick={() => setAdjustParticipationDialogOpen(true)}
                              id={'adjust-participation-button-id'}
                            >
                              <FontAwesomeIcon size="xs" icon={faPencil} />
                            </IconButton>
                          </div>
                        </Tooltip>
                        {adjustParticipationDialogOpen && (
                          <AdjustParticipationDialog
                            rewardNumberParticipation={value || 0}
                            rewardNumberUsed={reward.promocodeStats?.wonCodes || 0}
                            onClose={() => setAdjustParticipationDialogOpen(false)}
                            onApply={onChange}
                          />
                        )}
                      </Box>
                    )}
                  />
                </Box>
              </>
            )}
            {participationValidityEditing ? (
              <QlfFormRow
                label={t('reward.campaign.dialog.participation.validity')}
                sxs={{
                  label: { marginTop: 0.8 },
                  row: { display: 'flex', alignItems: 'flex-start' },
                  field: { display: 'flex', alignItems: 'flex-start' },
                }}
              >
                <Controller
                  control={control}
                  name="data.accessData.validityPeriod.value"
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                    <Tooltip title={permissionTooltip}>
                      <QlfNumberField
                        {...field}
                        inputRef={ref}
                        placeholder={t('reward.campaign.dialog.participation.validity.value')}
                        sx={{ width: 70 }}
                        error={!!error}
                        helperText={error?.message}
                        disabled={!!selectedReward?.deletedAt}
                      />
                    </Tooltip>
                  )}
                />
                <Box sx={{ marginLeft: 1 }}>
                  <Controller
                    control={control}
                    name="data.accessData.validityPeriod.unit"
                    render={({ field: { ref, ...field } }) => (
                      <FormControl>
                        <Tooltip title={permissionTooltip}>
                          <TextField
                            select
                            {...field}
                            inputRef={ref}
                            disabled={!!selectedReward?.deletedAt}
                            sx={{
                              width: 110,
                            }}
                            value={field.value || periodValue[RewardPeriod.HOUR]}
                          >
                            {Object.keys(RewardPeriod).map(value => (
                              <MenuItem key={value} value={value} id={`reward_period_${value}`}>
                                {periodValue[value as RewardPeriod]}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Tooltip>
                      </FormControl>
                    )}
                  />
                </Box>
              </QlfFormRow>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      width: 180,
                    }}
                  >
                    {t('reward.campaign.dialog.participation.validity')}
                  </Typography>
                  <Typography variant="h3">
                    {reward?.data?.accessData.validityPeriod.value}
                  </Typography>
                  <Typography variant="body1" sx={{ marginLeft: 1 }}>
                    {reward?.data?.accessData?.validityPeriod?.unit
                      ? periodValue[reward?.data.accessData.validityPeriod.unit]
                      : ''}
                  </Typography>

                  <Tooltip title={permissionTooltip}>
                    <div>
                      <IconButton
                        disabled={!rewardWriteEnabled || !!selectedReward?.deletedAt}
                        className={classes.iconButton}
                        onClick={() => {
                          setParticipationValidityEditing(true);
                        }}
                        id={'adjust-participation-validity-button-id'}
                      >
                        <FontAwesomeIcon size="xs" icon={faPencil} />
                      </IconButton>
                    </div>
                  </Tooltip>
                </Box>
              </>
            )}
          </DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'right',
              paddingTop: '30px',
              marginLeft: '20px',
              marginRight: '20px',
            }}
          >
            <Button
              disabled={!enableWonExport || exporting}
              color="secondary"
              startIcon={<FontAwesomeIcon icon={faDownload} />}
              sx={{ marginRight: '10px' }}
              onClick={downloadExport}
            >
              {t('rewards.campaign-redeemer.button.export-won')}
            </Button>
            {!!rewarAttribute.promocodeStats?.wonCodes &&
              rewarAttribute.promocodeStats.wonCodes > 0 && (
                <SearchPromocodes doSearch={doSearch} ItemComponent={CampaignRedeemerListItem} />
              )}
          </Box>
        </div>
      </Box>
      <a ref={exportAnchorRef} style={{ display: 'none' }} />
    </div>
  );
}
